import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    NgZone,
    OnDestroy,
    signal,
    TemplateRef,
    viewChild,
} from "@angular/core"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {ActivatedRoute, Router} from "@angular/router"
import {FullPageFeedbackComponent} from "@common/components/full-page-feedback/full-page-feedback.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {jobTaskIcon} from "@common/helpers/jobs"
import {JobTaskState} from "@generated"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {TippyService} from "@ngneat/helipopper"
import {TippyInstance} from "@ngneat/helipopper/config"
import {JobRunTimelineGQL, JobRunTimelineRecordFragment} from "@platform/components/jobs/job-run-timeline/job-run-timeline.generated"
import {readMemoryStats, type TaskMemoryStats} from "@cm/job-nodes/memory-stats"
import {memoryStatsLines} from "@platform/components/jobs/memory-stats/helpers"
import {axisBottom, scaleTime, select, timeFormat, zoom, zoomTransform} from "d3"
import {switchMap} from "rxjs"

// `lane` is copied but never displayed: on screen the tooltip sits next to its lane, so the owning
// task is obvious — in pasted text it is not.
type TooltipData = {title: string; color: string; lines: string[]; lane: string}

// One heartbeat observation: the instance was confirmed alive at endMs (the observation time, which lines up
// with the lifecycle markers), having covered the preceding durationSeconds. Beats are not merged — each is
// drawn as its own bar, so the bar edges sit at real observation times instead of a backdated bucket boundary.
type Segment = {
    /** The owning lane's label, carried so a copied tooltip names its task. */
    lane: string
    startMs: number
    endMs: number
    machineType: string | null
    instanceName: string | null
    zone: string | null
    queueGroup: string | null
}
// resourceGroupId/advanceReason are set on cascade events: which group the task moved to, and why it
// had to move. Null on every other event type.
type Marker = {
    /** The owning lane's label, carried so a copied tooltip names its task. */
    lane: string
    atMs: number
    eventType: string
    message: string | null
    queueGroup: string | null
    performedByName: string | null
    resourceGroupId: string | null
    advanceReason: string | null
    /** Runner-reported memory statistics: on completions (the run's peak) and the memory events. */
    memoryStats: TaskMemoryStats | null
}
// legacyId/uuid are null for a lane whose task has been deleted: it shows "deleted" and is not clickable.
// isJob marks the parent job's own lane (its lifecycle actions — nudge/cancel/restart/…); it is pinned
// to the top, labelled "Job", and is not a task link.
type Lane = {id: string; label: string; legacyId: number | null; uuid: string | null; isJob: boolean; segments: Segment[]; markers: Marker[]}

// Records with a duration are "still running" observations (e.g. heartbeats): each becomes its own bar,
// sorted oldest-first so later beats are drawn last and overlap earlier ones (the later/right bar on top).
// Records without a duration are point-in-time lifecycle events (queued, failed, ...) shown as markers.
const buildLane = (
    id: string,
    label: string,
    legacyId: number | null,
    uuid: string | null,
    isJob: boolean,
    records: readonly JobRunTimelineRecordFragment[],
): Lane => {
    const segments: Segment[] = records
        .filter((record) => record.durationSeconds != null)
        .map((record) => {
            const endMs = new Date(record.observedAt).getTime()
            return {
                lane: label,
                startMs: endMs - (record.durationSeconds ?? 0) * 1000,
                endMs,
                machineType: record.machineType ?? null,
                instanceName: record.instanceName ?? null,
                zone: record.zone ?? null,
                queueGroup: record.queueGroup ?? null,
            }
        })
        .sort((a, b) => a.startMs - b.startMs)
    const markers: Marker[] = records
        .filter((record) => record.durationSeconds == null)
        .map((record) => ({
            lane: label,
            atMs: new Date(record.observedAt).getTime(),
            eventType: record.eventType,
            message: record.message ?? null,
            queueGroup: record.queueGroup ?? null,
            performedByName: record.performedByName ?? null,
            resourceGroupId: record.resourceGroupId ?? null,
            advanceReason: record.advanceReason ?? null,
            memoryStats: readMemoryStats(record.memoryStats) ?? null,
        }))
        .sort((a, b) => a.atMs - b.atMs)
    return {id, label, legacyId, uuid, isJob, segments, markers}
}

// Records arrive flat for the whole job, including the job's own lifecycle-action events and events
// from tasks that have since been deleted (taskLegacyId/taskUuid are null for those). They are grouped
// into one lane per task via the stable taskKey, so a deleted task still appears; a task that was
// deleted and recreated keeps a single lane, since its graph identity (taskKey) is preserved across the
// recreation. The parent job's own records (taskUuid === jobId) become a dedicated "Job" lane pinned to
// the top — `jobId` is the job's uuid, matched against each record's resolved task uuid.
export const buildLanes = (records: readonly JobRunTimelineRecordFragment[], jobId: string | null): Lane[] => {
    const groups = new Map<string, JobRunTimelineRecordFragment[]>()
    for (const record of records) {
        const existing = groups.get(record.taskKey)
        if (existing) existing.push(record)
        else groups.set(record.taskKey, [record])
    }
    const lanes = Array.from(groups, ([key, groupRecords]) => {
        // Null id/uuid means the task row is gone — the lane shows "deleted" and isn't clickable.
        const legacyId = groupRecords.find((record) => record.taskLegacyId != null)?.taskLegacyId ?? null
        const uuid = groupRecords.find((record) => record.taskUuid != null)?.taskUuid ?? null
        const isJob = uuid != null && uuid === jobId
        let label = "deleted"
        if (isJob) label = "Job"
        else if (legacyId != null) label = `${legacyId}`
        return buildLane(key, label, legacyId, uuid, isJob, groupRecords)
    }).filter((lane) => lane.segments.length > 0 || lane.markers.length > 0)

    // Always surface the parent job's own lane when the timeline has content, even if the job has no
    // job-level events yet — so it reads as "this is the job, no job actions so far" rather than vanishing.
    // Skipped when the timeline is otherwise empty (nothing to anchor the time axis) or the job id is unknown.
    if (jobId !== null && lanes.length > 0 && !lanes.some((lane) => lane.isJob)) {
        lanes.push(buildLane(`job-${jobId}`, "Job", null, jobId, true, []))
    }

    // Job lane first, then tasks by id ascending; deleted tasks (no id) sort to the end.
    return lanes.sort((a, b) => Number(b.isJob) - Number(a.isJob) || (a.legacyId ?? Infinity) - (b.legacyId ?? Infinity))
}

// Map a lifecycle event type onto the closest task state. Both the marker colour and the status icon
// derive from this, so the timeline matches the job tasks table exactly. Unmapped types fall back to
// grey and the question-mark glyph that jobTaskIcon(undefined) returns.
const eventState = (eventType: string): JobTaskState | undefined => {
    switch (eventType) {
        case "Completed":
            return JobTaskState.Complete
        case "Failed":
            return JobTaskState.Failed
        case "Fatal":
            return JobTaskState.Fatal
        case "Cancelled":
            return JobTaskState.Cancelled
        case "Preempted":
            return JobTaskState.Preempted
        case "TimedOut":
            return JobTaskState.Timeout
        // The runner crossed its memory threshold but is still running: yellow, like a preemption, since it
        // reads as "at risk" rather than failed. Killed for running out of memory is a hard failure — red.
        case "MemoryPressure":
            return JobTaskState.Preempted
        case "OutOfMemory":
            return JobTaskState.Failed
        // Queued/Invoked, plus the coordinator's Designated/Provisioned, share the "being readied" state.
        // The Nudged/Re-run job actions borrow this state's purple so they read as "re-queued".
        // The cascade events join them: advancing re-queues the task onto the next resource group, and
        // Skipped/Stockout record groups it never ran on at all. All three read as "being readied"
        // rather than as failures — deliberately, since the task has not failed; any real failure that
        // triggered the advance is recorded by its own preceding marker.
        case "Queued":
        case "Invoked":
        case "Designated":
        case "Provisioned":
        case "Nudged":
        case "Rerun":
        case "CascadeAdvanced":
        case "Skipped":
        case "Stockout":
        case "Misconfigured":
            return JobTaskState.Queued
        // Active: the JobsV2 coordinator gave the task a concurrency slot, so it is runnable.
        case "Runnable":
        case "Active":
            return JobTaskState.Runnable
        // Restarted borrows Running's blue (and its circle-play glyph) so a restart reads like "playing" again.
        case "Running":
        case "Progress":
        case "Heartbeat":
        case "Restarted":
            return JobTaskState.Running
        case "Created":
            return JobTaskState.Init
        default:
            return undefined
    }
}

// Marker colours mirror the $job-state-* palette in src/sass/settings.scss (resolved the same way as
// jobTaskStateColor), so a marker is the exact colour of its state in the job tasks table.
const STATE_COLOR_LIGHT = "#6f6f6f" // $job-state-light — Init/Runnable and any unmapped state
const STATE_COLOR: Partial<Record<JobTaskState, string>> = {
    [JobTaskState.Queued]: "#9763ff", // $purple ($job-state-runnable)
    [JobTaskState.Running]: "#40c4ff", // $blue ($job-state-running)
    [JobTaskState.Complete]: "#22a033", // $green ($job-state-success)
    [JobTaskState.Failed]: "#dc4731", // $red ($job-state-error)
    [JobTaskState.Timeout]: "#dc4731",
    [JobTaskState.Fatal]: "#dc4731",
    [JobTaskState.Cancelled]: "#ff3366", // $pink ($job-state-cancelled)
    [JobTaskState.Preempted]: "#f6b62b", // $yellow ($job-state-preempted)
}

const eventColor = (eventType: string): string => {
    const state = eventState(eventType)
    return (state !== undefined ? STATE_COLOR[state] : undefined) ?? STATE_COLOR_LIGHT
}

// Per-event icon overrides, for events whose mapped state doesn't yield the right glyph. Colour still
// comes from eventState, so these stay in the tasks-table palette: Provisioned + Nudged/Re-run = queued
// purple, Progress = running blue (the info glyph just distinguishes a progress ping from the
// Running-state marker), Updated = grey. Anything not listed falls back to the job-tasks-table status
// icon (jobTaskIcon). FA6-solid codepoints, same font as jobTaskIcon.
const EVENT_ICON_OVERRIDE: Record<string, string> = {
    // Designated and Provisioned share the Queued state (and so its colour), but they are distinct
    // steps \u2014 a VM was picked, then it was started. Without its own glyph, Designated is drawn
    // identically to the Invoked marker beside it and reads as a duplicate.
    Designated: "\uf192", // fa-circle-dot - a VM was assigned to the task, not yet started
    Provisioned: "\uf055", // fa-circle-plus - a coordinator-provisioned (newly added) runner
    Progress: "\uf05a", // fa-circle-info - progress ping (distinct from the Running-state marker)
    Nudged: "\uf0a9", // fa-circle-arrow-right - re-queued forward
    Rerun: "\uf0a8", // fa-circle-arrow-left - re-run / rewind
    Updated: "\uf05a", // fa-circle-info - metadata edit
    CascadeAdvanced: "\uf0aa", // fa-circle-arrow-up - escalated up the resource cascade
    // A group the task never ran on: declined by the cascader, or tried and refused for capacity.
    Skipped: "\uf056", // fa-circle-minus - the cascader declined to place the task here
    Stockout: "\uf056", // fa-circle-minus - asked the cloud for this group and was refused
    Misconfigured: "\uf0ad", // fa-wrench - the group's MIG/template does not exist
    // Retired from its slot; grey (no mapped state) since it is "set aside", not a failure.
    Discarded: "\uf057", // fa-circle-xmark
    // Memory events keep their state colours (yellow / red) but get memory glyphs, so a pressure warning is
    // not mistaken for a preemption and an OOM kill not for a generic failure.
    MemoryPressure: "", // fa-gauge-high - memory usage crossed the warning threshold
    OutOfMemory: "", // fa-memory - killed for running out of memory
}

// An explicit per-event override wins; otherwise reuse the job-tasks-table status icon for the mapped state.
const eventIcon = (eventType: string): string => EVENT_ICON_OVERRIDE[eventType] ?? jobTaskIcon(eventState(eventType))

// Display-name overrides for the timeline tooltips. "Active" → "Activated" matches the past-tense
// lifecycle wording; "Escalated" is plainer than the raw CascadeAdvanced enum name and says what
// actually happened: the task moved up its resource cascade to a more available (and more expensive) group.
const EVENT_LABEL_OVERRIDE: Record<string, string> = {
    Active: "Activated",
    CascadeAdvanced: "Escalated",
    Misconfigured: "Misconfigured group",
    MemoryPressure: "Memory pressure",
    OutOfMemory: "Out of memory",
}
const eventLabel = (eventType: string): string => EVENT_LABEL_OVERRIDE[eventType] ?? eventType

// Why the cascade advanced, as a clause that completes "advanced because …". The enum values are terse;
// spelling them out keeps the timeline self-explanatory without cross-referencing the backend. The
// distinction they draw is whether we asked the cloud and were refused (NoCapacity) or never asked at
// all (the other two), which is what makes a placement path readable after the fact.
const ADVANCE_REASON_CLAUSE: Record<string, string> = {
    NoCapacity: "the cloud had no capacity for that group",
    PreemptionBlacklist: "that group's preemption circuit breaker was open",
    PreviousFailure: "this task had already failed on that group",
    Misconfigured: "that group is misconfigured (its MIG or template does not exist)",
}

const segmentTooltipLines = (segment: Segment): string[] => {
    const durationS = Math.round((segment.endMs - segment.startMs) / 1000)
    return [
        ...(segment.queueGroup ? [`queue group: ${segment.queueGroup}`] : []),
        `${segment.instanceName ?? "?"} (${segment.machineType ?? "?"}, ${segment.zone ?? "?"})`,
        `${new Date(segment.startMs).toLocaleString()} → ${new Date(segment.endMs).toLocaleString()}`,
        `heartbeat · ~${durationS}s`,
    ]
}

const markerTooltipLines = (marker: Marker): string[] => [
    ...(marker.resourceGroupId ? [`resource group: ${marker.resourceGroupId}`] : []),
    ...(marker.advanceReason ? [`advanced because ${ADVANCE_REASON_CLAUSE[marker.advanceReason] ?? marker.advanceReason}`] : []),
    ...(marker.queueGroup ? [`queue group: ${marker.queueGroup}`] : []),
    ...(marker.memoryStats ? memoryStatsLines(marker.memoryStats) : []),
    ...(marker.message ? [marker.message] : []),
    ...(marker.performedByName ? [`by ${marker.performedByName}`] : []),
    new Date(marker.atMs).toLocaleString(),
]

const MARGIN = {top: 8, right: 16, bottom: 24, left: 108}
const LANE_HEIGHT = 28
const BAR_HEIGHT = 14
// Heartbeat/run segments use a faint blue: the instance is shown in the tooltip and a per-machine palette would
// clash with the lifecycle-status colours used for the event icons.
const SEGMENT_COLOR = "rgb(236, 249, 255)"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-job-run-timeline",
    imports: [FullPageFeedbackComponent],
    templateUrl: "./job-run-timeline.component.html",
    styleUrl: "./job-run-timeline.component.scss",
})
export class JobRunTimelineComponent implements AfterViewInit, OnDestroy {
    readonly $jobId = input.required<string>({alias: "jobId"})
    private readonly $container = viewChild.required<ElementRef<HTMLDivElement>>("container")
    private readonly $timelineTooltip = viewChild.required("timelineTooltip", {read: TemplateRef})

    private readonly gql = inject(JobRunTimelineGQL)
    private readonly destroyRef = inject(DestroyRef)
    private readonly tippyService = inject(TippyService)
    private readonly notifications = inject(NotificationsService)
    private readonly router = inject(Router)
    private readonly route = inject(ActivatedRoute)
    private readonly zone = inject(NgZone)

    protected readonly $lanes = signal<Lane[] | undefined>(undefined)
    protected readonly $tooltipData = signal<TooltipData | undefined>(undefined)

    private tooltipInstances: TippyInstance[] = []
    private resizeObserver?: ResizeObserver
    private viewReady = false

    constructor() {
        toObservable(this.$jobId)
            .pipe(
                switchMap((id) => fetchThrowingErrors(this.gql)({id})),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({job}) => this.$lanes.set(buildLanes(job?.taskEvents ?? [], job?.id ?? null)))

        effect(() => {
            const lanes = this.$lanes()
            if (this.viewReady && lanes?.length) {
                this.render(lanes)
            }
        })
    }

    ngAfterViewInit() {
        this.viewReady = true
        this.resizeObserver = new ResizeObserver(() => {
            const lanes = this.$lanes()
            if (lanes?.length) {
                this.render(lanes)
            }
        })
        this.resizeObserver.observe(this.$container().nativeElement)
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect()
        this.destroyTooltips()
    }

    // The job task detail route ":jobTaskId" is a child of this job's route, so navigate relative to it
    // with the task uuid (the route resolves tasks by uuid, not legacy id). d3 click handlers run outside
    // Angular's zone, so re-enter it for navigation to trigger change detection.
    private openTask(uuid: string) {
        void this.zone.run(() => this.router.navigate([uuid], {relativeTo: this.route, queryParamsHandling: "preserve"}))
    }

    // Open the instance's merged lifecycle (task + instance events). Absolute route — the instance view is
    // a sibling of the job route, not a child of this task. d3 handlers run outside Angular's zone.
    private openInstance(instanceName: string) {
        void this.zone.run(() => this.router.navigate(["/jobs", "instance", instanceName], {queryParamsHandling: "preserve"}))
    }

    private destroyTooltips() {
        this.tooltipInstances.forEach((instance) => instance.destroy())
        this.tooltipInstances = []
    }

    /**
     * Copy what the tooltip shows — title and all detail lines — to the clipboard. Timeline markers
     * carry the failure messages needed to debug a run, and a tooltip cannot be selected with the
     * mouse, so clicking is the only way to get the text out.
     */
    private async copyTooltip(data: TooltipData): Promise<void> {
        // "Job" is already a whole label; a task lane is just its id, so name it as one.
        const lane = data.lane === "Job" ? "Job" : `Task ${data.lane}`
        await navigator.clipboard.writeText([`${lane} — ${data.title}`, ...data.lines].join("\n"))
        this.notifications.showInfo("Copied to clipboard")
    }

    private attachTooltip(element: SVGElement, data: TooltipData) {
        // Both markers and segments funnel through here, so one listener covers the whole timeline.
        element.style.cursor = "pointer"
        element.addEventListener("click", () => void this.copyTooltip(data))
        this.tippyService
            .create(element as unknown as HTMLElement, this.$timelineTooltip(), {
                theme: "light",
                animation: "scale-subtle",
                onShow: (instance: TippyInstance) => this.$tooltipData.set(instance.data),
            })
            .subscribe((instance) => {
                instance.data = data
                this.tooltipInstances.push(instance)
            })
    }

    private render(lanes: Lane[]) {
        const container = this.$container().nativeElement
        const width = container.clientWidth || 640
        const innerWidth = Math.max(width - MARGIN.left - MARGIN.right, 50)
        const height = MARGIN.top + lanes.length * LANE_HEIGHT + MARGIN.bottom

        const times = lanes.flatMap((lane) => [...lane.segments.flatMap((s) => [s.startMs, s.endMs]), ...lane.markers.map((m) => m.atMs)])
        const startMs = Math.min(...times)
        const endMs = Math.max(...times)
        const padding = Math.max((endMs - startMs) * 0.02, 1000)
        const baseScale = scaleTime([new Date(startMs - padding), new Date(endMs + padding)], [0, innerWidth])

        // d3 reuses the SVG element across zoom redraws, but a full render() rebuilds it — so tear down the
        // tippy instances bound to the old elements first to avoid leaks and stale references.
        this.destroyTooltips()

        const root = select(container)
        root.selectAll("svg").remove()
        const svg = root.append("svg").attr("width", width).attr("height", height)

        const clipId = "cm-timeline-clip"
        svg.append("clipPath")
            .attr("id", clipId)
            .append("rect")
            .attr("width", innerWidth)
            .attr("height", lanes.length * LANE_HEIGHT)

        // Clip the label gutter so long labels are cut off at the gutter edge instead of overflowing onto the lanes.
        const gutterClipId = "cm-timeline-gutter-clip"
        svg.append("clipPath")
            .attr("id", gutterClipId)
            .append("rect")
            .attr("x", -8)
            .attr("width", MARGIN.left - 16)
            .attr("height", lanes.length * LANE_HEIGHT)

        const gutter = svg.append("g").attr("transform", `translate(8,${MARGIN.top})`).attr("clip-path", `url(#${gutterClipId})`)
        // SVG text has no text-overflow, so trim each label character-by-character until it fits, then add an ellipsis.
        const maxLabelWidth = MARGIN.left - 24
        gutter
            .selectAll<SVGTextElement, Lane>("text.cm-lane-label")
            .data(lanes)
            .join("text")
            .attr("class", "cm-lane-label")
            // The job lane is the current view: labelled distinctly and not a link. Live tasks link to
            // their detail view; deleted tasks ("deleted" label) are greyed and inert.
            .classed("cm-lane-label--job", (lane) => lane.isJob)
            .classed("cm-lane-label--link", (lane) => !lane.isJob && lane.uuid != null)
            .classed("cm-lane-label--deleted", (lane) => !lane.isJob && lane.uuid == null)
            .on("click", (_event, lane) => {
                if (!lane.isJob && lane.uuid) this.openTask(lane.uuid)
            })
            .attr("x", 0)
            .attr("y", (_, index) => index * LANE_HEIGHT + LANE_HEIGHT / 2)
            .attr("dominant-baseline", "central")
            .text((lane) => lane.label)
            .each(function (lane) {
                if (this.getComputedTextLength() <= maxLabelWidth) {
                    return
                }
                let text = lane.label
                while (text.length > 1 && this.getComputedTextLength() > maxLabelWidth) {
                    text = text.slice(0, -1)
                    this.textContent = `${text}…`
                }
            })

        const plot = svg.append("g").attr("transform", `translate(${MARGIN.left},${MARGIN.top})`)

        plot.selectAll("rect.cm-lane-bg")
            .data(lanes)
            .join("rect")
            .attr("class", (_, index) => `cm-lane-bg ${index % 2 ? "cm-lane-bg--odd" : ""}`)
            .attr("x", 0)
            .attr("y", (_, index) => index * LANE_HEIGHT)
            .attr("width", innerWidth)
            .attr("height", LANE_HEIGHT)

        const drawLayer = plot.append("g").attr("clip-path", `url(#${clipId})`)
        const axisGroup = svg
            .append("g")
            .attr("class", "cm-axis")
            .attr("transform", `translate(${MARGIN.left},${MARGIN.top + lanes.length * LANE_HEIGHT})`)

        const drawAxis = (scale: typeof baseScale) =>
            axisGroup.call(
                axisBottom(scale)
                    .ticks(Math.max(Math.floor(innerWidth / 110), 2))
                    .tickFormat((value) => timeFormat("%H:%M:%S")(value as Date)),
            )

        const draw = (scale: typeof baseScale) => {
            const lane = drawLayer
                .selectAll<SVGGElement, Lane>("g.cm-lane")
                .data(lanes)
                .join("g")
                .attr("class", "cm-lane")
                .attr("transform", (_, index) => `translate(0,${index * LANE_HEIGHT})`)

            // One bar per heartbeat, spanning its full [startMs, endMs] interval. Bars are bound oldest-first,
            // so d3 appends later beats last and they overlap earlier ones — the later (right) bar drawn on top.
            lane.selectAll<SVGRectElement, Segment>("rect.cm-segment")
                .data((laneDatum) => laneDatum.segments)
                .join("rect")
                .attr("class", "cm-segment")
                .attr("y", (LANE_HEIGHT - BAR_HEIGHT) / 2)
                .attr("height", BAR_HEIGHT)
                .attr("rx", BAR_HEIGHT / 2)
                .attr("fill", SEGMENT_COLOR)
                .attr("x", (segment) => scale(new Date(segment.startMs)))
                .attr("width", (segment) => Math.max(scale(new Date(segment.endMs)) - scale(new Date(segment.startMs)), 2))

            // Each marker is its own group of [white background disc, status icon, transparent hit circle], so a
            // background always sits immediately behind its own icon. Drawing them as separate flat layers would let a
            // later event's icon cover an earlier event's background, making the earlier icon's cutout show through.
            const marker = lane
                .selectAll<SVGGElement, Marker>("g.cm-event")
                .data((laneDatum) => laneDatum.markers)
                .join((enter) => {
                    const group = enter.append("g").attr("class", "cm-event")
                    // White disc so the glyph's negative space (e.g. the checkmark cutout) reads white.
                    group
                        .append("circle")
                        .attr("class", "cm-event-bg")
                        .attr("cy", LANE_HEIGHT / 2)
                        .attr("r", 6)
                        .attr("fill", "#ffffff")
                    group
                        .append("text")
                        .attr("class", "cm-event-icon")
                        .attr("y", LANE_HEIGHT / 2)
                        .attr("text-anchor", "middle")
                        .attr("dominant-baseline", "central")
                        .attr("fill", (datum) => eventColor(datum.eventType))
                        .text((datum) => eventIcon(datum.eventType))
                    // A larger transparent circle on top gives the marker a comfortable hover hitbox for the tooltip.
                    group
                        .append("circle")
                        .attr("class", "cm-event-hit")
                        .attr("cy", LANE_HEIGHT / 2)
                        .attr("r", 11)
                        .attr("fill", "transparent")
                    return group
                })
            marker.attr("transform", (datum) => `translate(${scale(new Date(datum.atMs))},0)`)
        }

        drawAxis(baseScale)
        draw(baseScale)

        // Attach tippy tooltips once after the initial draw. Zoom redraws reuse these same DOM elements (d3 join),
        // so the instances stay valid and tippy re-reads their position on show. Lane labels carry no tooltip —
        // they show the task id (or "deleted") and link to the task detail instead.
        drawLayer
            .selectAll<SVGRectElement, Segment>("rect.cm-segment")
            // A running segment is a task on a specific VM; make it a link to that instance's history.
            .style("cursor", (segment) => (segment.instanceName ? "pointer" : null))
            .on("click", (_event, segment) => {
                if (segment.instanceName) this.openInstance(segment.instanceName)
            })
            .each((segment, index, nodes) => {
                this.attachTooltip(nodes[index], {title: "Running", color: "#1565c0", lines: segmentTooltipLines(segment), lane: segment.lane})
            })
        drawLayer.selectAll<SVGCircleElement, Marker>("circle.cm-event-hit").each((marker, index, nodes) => {
            this.attachTooltip(nodes[index], {
                title: eventLabel(marker.eventType),
                color: eventColor(marker.eventType),
                lines: markerTooltipLines(marker),
                lane: marker.lane,
            })
        })

        // Wheel-zoom only with ctrl/⌘ held, so a plain vertical wheel keeps scrolling the surrounding
        // dialog (d3 leaves filtered-out events untouched — no preventDefault — so they bubble normally).
        // A trackpad pinch arrives as a wheel event with ctrlKey set, so pinch-to-zoom needs no modifier.
        // Drag-to-pan and double-click-to-zoom keep d3's defaults (any non-secondary button).
        const zoomBehavior = zoom<SVGSVGElement, unknown>()
            .scaleExtent([1, 500])
            .translateExtent([
                [0, 0],
                [innerWidth, height],
            ])
            .extent([
                [0, 0],
                [innerWidth, height],
            ])
            .filter((event: MouseEvent) => (event.type === "wheel" ? event.ctrlKey || event.metaKey : !event.button))
            .on("zoom", (event) => {
                const scaled = event.transform.rescaleX(baseScale)
                drawAxis(scaled)
                draw(scaled)
            })
        svg.call(zoomBehavior)

        // A mostly-horizontal wheel (two-finger swipe, wheel tilt) pans the timeline. Consuming the event
        // also keeps the browser's swipe-to-navigate-back/forward gesture from firing over the chart —
        // preventDefault is needed for that even at scale 1, where the pan itself is a clamped no-op.
        // Mostly-vertical wheels fall through to the dialog scroll; ctrl/⌘ wheels are the zoom path above.
        svg.on(
            "wheel.pan",
            (event: WheelEvent) => {
                if (event.ctrlKey || event.metaKey) return
                if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return
                event.preventDefault()
                const node = svg.node()
                // translateBy pre-scales by k: divide so the view tracks the gesture 1:1 in screen pixels.
                if (node !== null) zoomBehavior.translateBy(svg, -event.deltaX / zoomTransform(node).k, 0)
            },
            {passive: false},
        )
    }
}
